import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, ShieldCheck } from "lucide-react";
import { SubmitButton } from "@/components/form/SubmitButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/AuthContext";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

/**
 * Super Admin login portal — /superadmin/login (also mapped from /admin/login).
 *
 * Strict role isolation: backend rejects any account whose role !== 'super_admin'.
 */
export default function AdminLogin() {
    const { login, user, status } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // If a super_admin session already exists, bounce to dashboard.
    useEffect(() => {
        if (status !== "authenticated" || !user) return;
        if (user.role === "super_admin" || user.role === "admin") {
            navigate(ROUTES.adminDashboard, { replace: true });
        }
    }, [status, user, navigate]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await login({
                email: email.trim(),
                password,
                remember: true,
                portal: "superadmin",
            });
            const redirect = (location.state as { from?: string } | null)?.from;
            navigate(
                redirect && redirect.startsWith("/admin") ? redirect : ROUTES.adminDashboard,
                { replace: true },
            );
            toast.success("Welcome, Super Admin");
        } catch (err) {
            setError(formatApiError(err, "Sign-in failed — please try again."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="superadmin-login-page" className="space-y-8">
            <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ink text-white">
                    <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                        Super Admin
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Full-network access — TSGo owner only.
                    </p>
                </div>
            </div>

            <form className="space-y-5" onSubmit={onSubmit} aria-label="Super admin login form">
                <div className="space-y-1.5">
                    <Label htmlFor="admin-email">Super Admin email</Label>
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="admin-email"
                            type="email"
                            placeholder="you@tsgo.example"
                            className="pl-9"
                            data-testid="admin-login-email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="admin-password">Password</Label>
                    <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="admin-password"
                            type="password"
                            placeholder="••••••••"
                            className="pl-9"
                            data-testid="admin-login-password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>
                </div>

                {error ? (
                    <p role="alert" data-testid="admin-login-error" className="text-sm font-medium text-error">
                        {error}
                    </p>
                ) : null}

                <SubmitButton
                    type="submit"
                    loading={submitting}
                    className="bg-ink text-white hover:bg-ink/90"
                    data-testid="admin-login-submit"
                >
                    Sign in
                </SubmitButton>
            </form>

            <p className="text-center text-xs text-muted-foreground">
                Access to this area is logged and audited.
            </p>
        </div>
    );
}
